// 2026-05-25 帳務管理 — 全面改版（截圖一/二 design）
// 結構：
//   page title「帳務管理」
//   2-col 上層：
//     左：帳號資訊 card + 最新一筆付款 card (stacked)
//     右：近期消費 card (full height)
//   篩選列：開始日期 / 結束日期 / 項目 dropdown
//   搜尋框
//   帳單明細 sub-card：header (匯出 Excel + 顯示) + 7 cols table + footer
//
// API: GET /users/billing_profile (個人/最新付款) + /users/invoices (列表/明細) + /users/invoices/:n/download (PDF)
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  TextField,
  MenuItem,
  Menu,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import AutoRenewToggle from "components/mas/AutoRenewToggle";
import { LanguageContext } from "../../../../context/LanguageContext";
import { trackEvent } from "utils/analytics";
import { COLORS, TYPO } from "theme/tokens";

// ⚠️ 這三個小元件放「模組層級」而非元件內部：放元件內每次 render 都會產生新的元件型別，
//    害子元件(尤其有 setState 的，如 AutoRenewToggle 的語系 effect)不斷卸載+重掛 → 無限迴圈卡死。
// PNG icon 小工具
const PngIcon = ({ src, size = 18 }) => (
  <Box
    component="img"
    src={src}
    alt=""
    sx={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }}
    onError={(e) => {
      e.target.style.display = "none";
    }}
  />
);

// 卡框：圓角12 / 1px灰框 / 白底 / shadow-M（依 Sean spec）
const SubCard = ({ title, children, ...rest }) => (
  <Card
    sx={{
      p: "24px",
      borderRadius: "12px",
      boxShadow: "2px 2px 8px 0 rgba(60, 59, 59, 0.15)",
      border: `1px solid ${COLORS.grayLighter}`,
      background: COLORS.white,
      ...rest.sx,
    }}
  >
    {title && (
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ width: 3, height: 18, background: COLORS.main, borderRadius: 1 }} />
        <MDTypography sx={{ ...TYPO.title, fontWeight: 700, color: COLORS.black }}>
          {title}
        </MDTypography>
      </Box>
    )}
    {children}
  </Card>
);

// label/value 皆 bodyM；label 灰、value 黑（icon 由呼叫端傳 PngIcon）
const InfoRow = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" gap={1.5} mb={1.2}>
    <Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
    <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.grayDark }}>{label}：</MDTypography>
    <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>
      {value || "-"}
    </MDTypography>
  </Box>
);

export default function BillingManager() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("BillingManager", "billing/BillingManager/BillingManager_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.BillingManager || { Main: {}, Error: {} },
    [componentLanguageData]
  );

  // ===== state =====
  const [profile, setProfile] = useState(null);
  const [myPlan, setMyPlan] = useState(null); // /plans/me/plan → 自動續費開關的狀態來源
  const [invoices, setInvoices] = useState([]);
  const [loadingDownload, setLoadingDownload] = useState(false);

  // 篩選 + 分頁（前端做）
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [itemFilter, setItemFilter] = useState("ALL"); // ALL / PLATFORM / COIN
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [anchorElPageSize, setAnchorElPageSize] = useState(null);

  // ===== fetch =====
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/users/billing_profile");
        setProfile(res.data);
        // GA4 purchase：付款導回帳單頁時，偵測最新一筆付款回報一次。
        //   去重靠 merchantTradeNo（存 localStorage）＋ 24h 時間窗（避免老用戶陳年付款被當新付款誤報）。
        try {
          const rec = res.data?.user?.latestPaymentRecord;
          if (rec?.merchantTradeNo && rec?.paidAt) {
            const reportedTxn = localStorage.getItem("ga_last_purchase_txn");
            const ageMs = Date.now() - new Date(rec.paidAt).getTime();
            if (rec.merchantTradeNo !== reportedTxn && ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000) {
              trackEvent("purchase", {
                transaction_id: rec.merchantTradeNo,
                value: rec.amount,
                currency: "TWD",
                plan: res.data?.user?.planCode,
                payment_type: rec.paymentType,
              });
              localStorage.setItem("ga_last_purchase_txn", rec.merchantTradeNo);
            }
          }
        } catch (e) {
          /* localStorage/解析失敗不影響頁面 */
        }
      } catch (err) {
        console.error(LANGUAGE.Error.BillingFailed, err);
      }
    })();
    (async () => {
      try {
        const res = await axiosInstance.get("/users/invoices");
        setInvoices(res.data || []);
      } catch (err) {
        console.error(LANGUAGE.Error.InvoiceFailed, err);
      }
    })();
  }, [LANGUAGE.Error.BillingFailed, LANGUAGE.Error.InvoiceFailed]);

  // 自動續費開關需要 /plans/me/plan 的狀態（autoRenewEnabled / planCode / billingCycle）
  const fetchMyPlan = async () => {
    try {
      const res = await axiosInstance.get("/plans/me/plan");
      setMyPlan(res.data);
    } catch (err) {
      console.error("fetch myPlan failed", err);
    }
  };
  useEffect(() => {
    fetchMyPlan();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== handlers =====
  const downloadInvoice = async (invoiceNumber) => {
    setLoadingDownload(true);
    try {
      const res = await axiosInstance.get(`/users/invoices/${invoiceNumber}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(LANGUAGE.Error.DownloadFailed, err);
    } finally {
      setLoadingDownload(false);
    }
  };

  const handleExportExcel = () => {
    const exportData = filteredInvoices.map((inv) => ({
      [LANGUAGE.Main.ColTime || "時間"]: dayjs(inv.issuedAt).format("YYYY-MM-DD"),
      [LANGUAGE.Main.ColReceipt || "收據號碼"]: inv.invoiceNumber,
      [LANGUAGE.Main.ColOrder || "訂單編號"]: inv.orderNumber || inv.merchantTradeNo || "-",
      [LANGUAGE.Main.ColItem || "項目"]: getItemLabel(inv),
      [LANGUAGE.Main.ColDesc || "說明"]: getDescLabel(inv),
      [LANGUAGE.Main.ColAmount || "金額 (USD)"]: inv.amount,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BillingDetails");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `billing_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`
    );
  };

  // ===== derived =====
  const user = profile?.user || {};
  const latestPayment = user.latestPaymentRecord || null;

  // 從 invoice 推「項目」「說明」：依 paymentType 區分（PLAN=會員方案、RECHARGE=Coin 儲值）
  //   （原本兩個三元分支被寫成一樣 + 判斷用了不存在的 invoiceType → 說明永遠變 Coin，此為修正）
  const isCoinInvoice = (inv) =>
    inv.paymentType === "RECHARGE" ||
    inv.invoiceType === "COIN" ||
    (inv.note && inv.note.includes("Coin"));
  const getItemLabel = (inv) =>
    isCoinInvoice(inv)
      ? LANGUAGE.Main.ItemCoinRecharge || "MAS Coin 儲值費用"
      : LANGUAGE.Main.ItemMasPlatform || "MAS 平台費用";
  const getDescLabel = (inv) =>
    isCoinInvoice(inv)
      ? LANGUAGE.Main.ItemCoinRecharge || "MAS Coin 儲值費用"
      : LANGUAGE.Main.ItemPlanFee || "MAS 會員方案費用";

  // 篩選後的 invoice list
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const d = dayjs(inv.issuedAt);
      if (startDate && d.isBefore(dayjs(startDate), "day")) return false;
      if (endDate && d.isAfter(dayjs(endDate), "day")) return false;
      // 修補：itemFilter(ALL/PLATFORM/COIN) 原本沒套用 → 下拉切換沒反應。
      if (itemFilter === "COIN" && !isCoinInvoice(inv)) return false;
      if (itemFilter === "PLATFORM" && isCoinInvoice(inv)) return false;
      if (keyword) {
        const k = keyword.toLowerCase();
        if (
          !(inv.invoiceNumber || "").toLowerCase().includes(k) &&
          !(inv.orderNumber || inv.merchantTradeNo || "").toLowerCase().includes(k)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [invoices, startDate, endDate, keyword, itemFilter]);

  // 近期消費（前 4 筆）
  const recentInvoices = useMemo(() => invoices.slice(0, 4), [invoices]);

  // 分頁
  const totalRows = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pagedRows = filteredInvoices.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, itemFilter, keyword, pageSize]);

  const footerText = (LANGUAGE.Main.Footer || "顯示 {{from}}-{{to}} 筆，共 {{total}} 筆資料")
    .replace("{{from}}", totalRows === 0 ? 0 : (page - 1) * pageSize + 1)
    .replace("{{to}}", Math.min(page * pageSize, totalRows))
    .replace("{{total}}", totalRows);

  // 通用 input 樣式
  const inputSx = {
    "& .MuiInputBase-root": {
      borderRadius: "6px",
      backgroundColor: COLORS.grayLightest,
      ...TYPO.bodyM,
      color: COLORS.black,
      minHeight: "52px",
    },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiInputLabel-root": { ...TYPO.bodyS, color: COLORS.black },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
    },
  };

  // ─── 子元件 ───
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3} sx={{ background: COLORS.pageBg, minHeight: "100vh" }}>
        {/* page title */}
        <MDTypography sx={{ fontSize: "20px", fontWeight: 700, color: COLORS.black, mb: 2 }}>
          {LANGUAGE.Main.PageTitle || "帳務管理"}
        </MDTypography>

        {/* 2026-05-25 拿掉外層大白卡，每個區塊獨立白卡直接放灰底上 */}
        <Box>
          {/* 上層 2-col */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            {/* 左 col：帳號資訊 + 最新一筆付款 (stacked) */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SubCard title={LANGUAGE.Main.AccountInfo || "帳號資訊"}>
                <InfoRow
                  icon={<PngIcon src="/images/icon/mail.png" />}
                  label={LANGUAGE.Main.Email || "Email"}
                  value={user.email}
                />
                <InfoRow
                  icon={<PngIcon src="/images/icon/User.png" />}
                  label={LANGUAGE.Main.CurrentPlan || "目前方案"}
                  value={user.planName || user.planCode}
                />
                <Box mt={1.5}>
                  <AutoRenewToggle
                    myPlan={myPlan}
                    onChanged={fetchMyPlan}
                    labelSx={{ ...TYPO.title, fontWeight: 500, color: COLORS.black }}
                  />
                </Box>
              </SubCard>

              <SubCard title={LANGUAGE.Main.LatestPayment || "最新一筆付款"}>
                {latestPayment ? (
                  // 2026-05-25 內容包灰底框（依截圖二 design）
                  <Box
                    sx={{
                      backgroundColor: COLORS.grayLightest,
                      borderRadius: "8px",
                      p: "16px",
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <PngIcon src="/images/icon/Wallet.png" />
                        <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.grayDark }}>
                          {LANGUAGE.Main.PayMethod || "支付方式"}：
                        </MDTypography>
                        <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>
                          {latestPayment.paymentType === "SUBSCRIPTION"
                            ? (LANGUAGE.Main.PayTypeSubscription || "訂閱付款")
                            : latestPayment.paymentType === "ONE_TIME"
                            ? (LANGUAGE.Main.PayTypeOneTime || "一次性付款")
                            : latestPayment.paymentType}
                        </MDTypography>
                      </Box>
                      <MDTypography sx={{ ...TYPO.title, fontWeight: 500, color: COLORS.black }}>
                        {dayjs(latestPayment.paidAt).format("YYYY/M/D")}
                      </MDTypography>
                    </Box>
                    <InfoRow
                      icon={<PngIcon src="/images/icon/Receipt.png" />}
                      label={LANGUAGE.Main.OrderNumber || "訂單編號"}
                      value={latestPayment.merchantTradeNo}
                    />
                    <Box sx={{ mb: 0 }}>
                      <InfoRow
                        icon={<PngIcon src="/images/icon/Receipt.png" />}
                        label={LANGUAGE.Main.ReceiptNumber || "收據號碼"}
                        value={invoices[0]?.invoiceNumber || "-"}
                      />
                    </Box>
                  </Box>
                ) : (
                  <MDTypography sx={{ fontSize: "13px", color: COLORS.grayDark }}>—</MDTypography>
                )}
              </SubCard>
            </Box>

            {/* 右 col：近期消費（高度撐滿 = 左邊兩張 card 加起來） */}
            <SubCard title={LANGUAGE.Main.RecentSpending || "近期消費"} sx={{ height: "100%" }}>
              {recentInvoices.length === 0 ? (
                <MDTypography sx={{ fontSize: "13px", color: COLORS.grayDark, textAlign: "center", py: 4 }}>
                  {LANGUAGE.Main.Empty || "目前尚無資料"}
                </MDTypography>
              ) : (
                recentInvoices.map((inv, idx) => (
                  <Box
                    key={inv.id || idx}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.4fr auto auto",
                      gap: 2,
                      py: 1.5,
                      borderBottom: idx < recentInvoices.length - 1 ? "1px solid #f5f5f5" : "none",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <MDTypography sx={{ ...TYPO.title, fontWeight: 500, color: "#1F2937" }}>
                        {dayjs(inv.issuedAt).format("YYYY/M/D")}
                      </MDTypography>
                      <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.grayDark }}>
                        {inv.invoiceNumber}
                      </MDTypography>
                    </Box>
                    <Box>
                      <MDTypography sx={{ ...TYPO.title, fontWeight: 500, color: "#1F2937" }}>
                        {getItemLabel(inv)}
                      </MDTypography>
                      <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.grayDark }}>
                        {getDescLabel(inv)}
                      </MDTypography>
                    </Box>
                    <MDTypography sx={{ ...TYPO.displayM, fontWeight: 500, color: COLORS.black }}>
                      ${inv.amount}
                    </MDTypography>
                    <IconButton
                      size="small"
                      onClick={() => downloadInvoice(inv.invoiceNumber)}
                      sx={{ p: 0.5 }}
                    >
                      <PngIcon src="/images/icon/Symbol.png" size={25} />
                    </IconButton>
                  </Box>
                ))
              )}
            </SubCard>
          </Box>

          {/* 外層白底大框：包 篩選列 + 搜尋 + 帳單明細（radius20 / 1px#E6E6E6）*/}
          <Box
            sx={{
              borderRadius: "20px",
              border: `1px solid ${COLORS.grayLighter}`,
              background: COLORS.white,
              p: 3,
            }}
          >
          {/* 篩選列 3 欄（label 在框外上方）*/}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.black, mb: 0.75 }}>
                {LANGUAGE.Main.StartDate || "開始日期"}
              </MDTypography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  format="YYYY-MM-DD"
                  slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
                />
              </LocalizationProvider>
            </Box>
            <Box>
              <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.black, mb: 0.75 }}>
                {LANGUAGE.Main.EndDate || "結束日期"}
              </MDTypography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  format="YYYY-MM-DD"
                  slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
                />
              </LocalizationProvider>
            </Box>
            <Box>
              <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.black, mb: 0.75 }}>
                {LANGUAGE.Main.Item || "項目"}
              </MDTypography>
              <TextField
                select
                fullWidth
                value={itemFilter}
                onChange={(e) => setItemFilter(e.target.value)}
                sx={inputSx}
              >
                <MenuItem value="ALL">{LANGUAGE.Main.ItemAll || "全部"}</MenuItem>
                <MenuItem value="PLATFORM">{LANGUAGE.Main.ItemMasPlatform || "MAS 平台費用"}</MenuItem>
                <MenuItem value="COIN">{LANGUAGE.Main.ItemCoinRecharge || "MAS Coin 儲值費用"}</MenuItem>
              </TextField>
            </Box>
          </Box>

          {/* 搜尋框（高52 / radius12 / 白底 / 右側48×52灰底 Search.png）*/}
          <Box sx={{ mb: 3, display: "flex", height: 52, width: "100%" }}>
            <TextField
              fullWidth
              placeholder={LANGUAGE.Main.SearchPlaceholder || "搜尋關鍵字..."}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ height: 52, maxHeight: 52, m: 0 }}>
                    <Box sx={{ width: 48, height: 52, background: COLORS.grayLightest, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                      <Box component="img" src="/images/icon/Search.png" alt="search" sx={{ width: 24, height: 24, objectFit: "contain" }} />
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 52,
                  borderRadius: "12px",
                  background: COLORS.white,
                  pr: 0,
                  overflow: "hidden",
                  "& fieldset": { borderColor: COLORS.grayLighter },
                  "&:hover fieldset": { borderColor: COLORS.grayMedian },
                  "&.Mui-focused fieldset": { borderColor: COLORS.main, borderWidth: "1px" },
                },
                "& .MuiOutlinedInput-input": {
                  ...TYPO.bodyM,
                  fontWeight: 400,
                  pl: "16px",
                  "&::placeholder": { color: COLORS.grayMedian, opacity: 1 },
                },
              }}
            />
          </Box>

          {/* 帳單明細 sub-card（radius12 / 1px#E6E6E6 / shadow-M）*/}
          <Card sx={{ borderRadius: "12px", boxShadow: "2px 2px 8px 0 rgba(60, 59, 59, 0.15)", border: `1px solid ${COLORS.grayLighter}`, overflow: "hidden" }}>
            {/* header bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 3,
                py: 2,
                borderBottom: "1px solid #eef0f3",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 3, height: 18, background: COLORS.main, borderRadius: 1 }} />
                <MDTypography sx={{ ...TYPO.title, fontWeight: 700, color: COLORS.black }}>
                  {LANGUAGE.Main.BillTitle || "帳單明細"}
                </MDTypography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <MDButton
                  variant="outlined"
                  size="small"
                  startIcon={<Box component="img" src="/images/icon/Export.png" alt="" sx={{ width: 16, height: 16, objectFit: "contain" }} />}
                  onClick={handleExportExcel}
                  sx={{
                    height: 30,
                    border: `1px solid ${COLORS.main}`,
                    color: `${COLORS.main} !important`,
                    background: COLORS.white,
                    ...TYPO.bodyS,
                    fontWeight: 400,
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 2,
                    boxShadow: "none",
                    "&:hover": { border: `1px solid ${COLORS.main}`, background: COLORS.white, boxShadow: "none" },
                  }}
                >
                  {LANGUAGE.Main.ExportExcel || "匯出 Excel"}
                </MDButton>
                <Box display="flex" alignItems="center" gap={1}>
                  <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>
                    {LANGUAGE.Main.Show || "顯示"}
                  </MDTypography>
                  <Box
                    onClick={(e) => setAnchorElPageSize(e.currentTarget)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      py: "10px",
                      px: "8px",
                      borderRadius: "6px",
                      background: COLORS.grayLightest,
                      cursor: "pointer",
                    }}
                  >
                    <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black, lineHeight: 1 }}>{pageSize}</MDTypography>
                    <Box component="img" src="/images/icon/Down.png" alt="" sx={{ width: 24, height: 24, objectFit: "contain" }} />
                  </Box>
                  <Menu
                    anchorEl={anchorElPageSize}
                    open={Boolean(anchorElPageSize)}
                    onClose={() => setAnchorElPageSize(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 80,
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
                        "& .MuiMenuItem-root": {
                          fontSize: "16px",
                          py: 1,
                          px: 2,
                          justifyContent: "center",
                          "&.Mui-selected": {
                            background: COLORS.lighter,
                            color: COLORS.main,
                            fontWeight: 600,
                          },
                          "&:hover": { background: COLORS.grayLightest },
                        },
                      },
                    }}
                  >
                    {[5, 10, 20, 50].map((n) => (
                      <MenuItem
                        key={n}
                        selected={pageSize === n}
                        onClick={() => {
                          setPageSize(n);
                          setAnchorElPageSize(null);
                        }}
                      >
                        {n}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
            </Box>

            {/* table - CSS Grid 7 cols */}
            <Box>
              {/* header row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "13% 18% 22% 13% 16% 12% 6%",
                  px: 3,
                  py: 1.5,
                  backgroundColor: COLORS.grayLightest,
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                {[
                  LANGUAGE.Main.ColTime || "時間",
                  LANGUAGE.Main.ColReceipt || "收據號碼",
                  LANGUAGE.Main.ColOrder || "訂單編號",
                  LANGUAGE.Main.ColItem || "項目",
                  LANGUAGE.Main.ColDesc || "說明",
                  LANGUAGE.Main.ColAmount || "金額 (USD)",
                  "",
                ].map((col, i) => (
                  <Box key={i} sx={{ ...TYPO.title, fontWeight: 700, color: COLORS.grayDark }}>
                    {col}
                  </Box>
                ))}
              </Box>

              {/* body rows */}
              {pagedRows.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center", color: COLORS.grayDark, fontSize: "13px" }}>
                  {LANGUAGE.Main.Empty || "目前尚無資料"}
                </Box>
              ) : (
                pagedRows.map((inv) => (
                  <Box
                    key={inv.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "13% 18% 22% 13% 16% 12% 6%",
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #f5f5f5",
                      transition: "background 0.15s",
                      "&:hover": { backgroundColor: COLORS.grayLightest },
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>
                      {dayjs(inv.issuedAt).format("YYYY-MM-DD")}
                    </Box>
                    <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{inv.invoiceNumber}</Box>
                    <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>
                      {inv.orderNumber || inv.merchantTradeNo || "-"}
                    </Box>
                    <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{getItemLabel(inv)}</Box>
                    <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{getDescLabel(inv)}</Box>
                    <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{inv.amount}</Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => downloadInvoice(inv.invoiceNumber)}
                        sx={{ p: 0.5 }}
                      >
                        <PngIcon src="/images/icon/Symbol.png" size={25} />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </Box>

            {/* footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 3,
                py: 2,
                borderTop: "1px solid #eef0f3",
              }}
            >
              <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.main }}>
                {footerText}
              </MDTypography>
              <Box display="flex" alignItems="center" gap={2}>
                {/* 往前一頁（arrow_right.png，依 Sean 指定）*/}
                <Box
                  component="button"
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ width: 48, height: 52, p: "14px 12px", border: "none", background: COLORS.grayLightest, display: "flex", alignItems: "center", justifyContent: "center", cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.4 : 1 }}
                >
                  <Box component="img" src="/images/icon/arrow_right.png" alt="prev" sx={{ width: 24, height: 24, objectFit: "contain" }} />
                </Box>
                <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{page}</MDTypography>
                {/* 往下一頁（arrow_left.png，依 Sean 指定）*/}
                <Box
                  component="button"
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  sx={{ width: 48, height: 52, p: "14px 12px", border: "none", background: COLORS.grayLightest, display: "flex", alignItems: "center", justifyContent: "center", cursor: page >= totalPages ? "default" : "pointer", opacity: page >= totalPages ? 0.4 : 1 }}
                >
                  <Box component="img" src="/images/icon/arrow_left.png" alt="next" sx={{ width: 24, height: 24, objectFit: "contain" }} />
                </Box>
              </Box>
            </Box>
          </Card>
          </Box>
        </Box>

        {/* 下載 loading overlay */}
        {loadingDownload && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress sx={{ color: COLORS.main }} />
          </Box>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
